import type { Feature, FeatureCollection, Polygon, MultiPolygon, Position } from "geojson";
import type { MapTool, MapClickEvent } from "../../../application/map/tools/types";
import type { ToolDeps } from "./types";
import { parsePolygonId, geoJSONKeyForType } from "../../helpers/JobMap/editHelpers";
import { cleanPolygonCoords } from "../engine/useMapDrawEngine";
import { ensureRingsClosed } from "../../helpers/JobMap/editHelpers";
import { editVerticesUseCase } from "../../../application/map/useCases/editVerticesUseCase";
import { isPointInPolygon } from "../../../domain/geometry/spatial/pointInPolygon";
import type { GeoJSONStoreFeatures } from "terra-draw";

export interface EditVerticesTool extends MapTool {
	save(): void;
	onMapDblClick(e: MapClickEvent): boolean;
}

interface ActivationState {
	jobId: string;
	typePrefix: string;
	featureIndex: number;
	pathIndex: number;
	originalFC: FeatureCollection<Polygon | MultiPolygon>;
	rings: Position[][];
	editingRingIndex: number;
}

export function makeEditVerticesTool(deps: ToolDeps): EditVerticesTool {
	let state: ActivationState | null = null;

	/** Snapshot current terra-draw edits back into state.rings */
	function snapshotCurrentRing() {
		if (!state) return;
		const snapshot = deps.drawEngine.getSnapshot();
		const edited = snapshot.find((f) => f.geometry.type === "Polygon");
		if (edited) {
			// ensureRingsClosed: terra-draw may strip the closing vertex
			const closed = ensureRingsClosed((edited.geometry as Polygon).coordinates);
			state.rings[state.editingRingIndex] = closed[0];
		}
	}

	/** Load a ring into terra-draw and show others as overlay */
	function loadRingIntoDrawEngine(ringIndex: number) {
		if (!state) return;

		// Capture current edits before switching
		snapshotCurrentRing();
		state.editingRingIndex = ringIndex;

		deps.drawEngine.clear();
		deps.toolOverlay.clearOverlay();

		// Show non-editing rings as overlay
		for (let i = 0; i < state.rings.length; i++) {
			if (i === ringIndex) continue;
			deps.toolOverlay.highlightFeature({
				type: "Feature",
				properties: { isHole: i > 0 },
				geometry: { type: "Polygon", coordinates: [state.rings[i]] },
			});
		}

		// Load target ring into terra-draw
		const prepared = {
			type: "Feature" as const,
			id: `edit-0`,
			properties: { mode: "polygon" },
			geometry: {
				type: "Polygon" as const,
				coordinates: cleanPolygonCoords([state.rings[ringIndex]]),
			},
		};

		deps.drawEngine.addFeatures([prepared as unknown as GeoJSONStoreFeatures]);
		deps.drawEngine.setMode("select");

		try {
			deps.drawEngine.selectFeature("edit-0");
		} catch {
			const snapshot = deps.drawEngine.getSnapshot();
			if (snapshot.length > 0 && snapshot[0].id != null) {
				deps.drawEngine.selectFeature(snapshot[0].id);
			}
		}
	}

	function activate() {
		const polygonId = deps.getSelectedPolygonId();
		if (!polygonId) {
			deps.showError("Select a polygon first");
			deps.deactivateTool();
			return;
		}

		const parsed = parsePolygonId(polygonId);
		if (!parsed) {
			deps.showError("Invalid polygon ID");
			deps.deactivateTool();
			return;
		}

		const jobId = deps.getSelectedJobId();
		if (!jobId) {
			deps.showError("No job selected");
			deps.deactivateTool();
			return;
		}

		const geoKey = geoJSONKeyForType(parsed.typePrefix);
		const fc =
			geoKey === "boundaryGeoJSON"
				? deps.getBoundaryFC(jobId)
				: geoKey === "zonesGeoJSON"
					? deps.getZonesFC(jobId)
					: null;

		if (!fc) {
			deps.showError("Geometry data not found");
			deps.deactivateTool();
			return;
		}

		const originalFC = fc as FeatureCollection<Polygon | MultiPolygon>;
		const feature = originalFC.features[parsed.featureIndex];
		if (!feature) {
			deps.showError("Feature not found at index");
			deps.deactivateTool();
			return;
		}

		// Extract all rings for the specific path
		const allRings =
			feature.geometry.type === "Polygon"
				? feature.geometry.coordinates
				: feature.geometry.coordinates[parsed.pathIndex];

		state = {
			jobId,
			typePrefix: parsed.typePrefix,
			featureIndex: parsed.featureIndex,
			pathIndex: parsed.pathIndex,
			originalFC,
			rings: allRings.map((r) => [...r]),
			editingRingIndex: 0,
		};

		// Show holes as overlay (don't snapshot — no prior edits)
		for (let i = 1; i < state.rings.length; i++) {
			deps.toolOverlay.highlightFeature({
				type: "Feature",
				properties: { isHole: true },
				geometry: { type: "Polygon", coordinates: [state.rings[i]] },
			});
		}

		// Load outer ring into terra-draw
		const prepared = {
			type: "Feature" as const,
			id: `edit-0`,
			properties: { mode: "polygon" },
			geometry: {
				type: "Polygon" as const,
				coordinates: cleanPolygonCoords([allRings[0]]),
			},
		};

		deps.drawEngine.clear();
		deps.drawEngine.addFeatures([prepared as unknown as GeoJSONStoreFeatures]);
		deps.drawEngine.setMode("select");

		try {
			deps.drawEngine.selectFeature("edit-0");
		} catch {
			const snapshot = deps.drawEngine.getSnapshot();
			if (snapshot.length > 0 && snapshot[0].id != null) {
				deps.drawEngine.selectFeature(snapshot[0].id);
			}
		}
	}

	function onMapDblClick(e: MapClickEvent): boolean {
		if (!state || state.rings.length <= 1) return false;

		const clickPoint: Position = [e.lngLat.lng, e.lngLat.lat];

		// Check each non-editing ring for hit
		for (let i = 0; i < state.rings.length; i++) {
			if (i === state.editingRingIndex) continue;
			const tempFeature: Feature<Polygon> = {
				type: "Feature",
				properties: {},
				geometry: { type: "Polygon", coordinates: [state.rings[i]] },
			};
			if (isPointInPolygon(clickPoint, tempFeature)) {
				loadRingIntoDrawEngine(i);
				return true;
			}
		}

		return false;
	}

	function save() {
		if (!state) return;

		// Capture current ring edits
		snapshotCurrentRing();

		const editedCoords = state.rings;
		const applyFC = state.typePrefix === "boundary" ? deps.applyBoundary : deps.applyZones;
		const { jobId, originalFC, featureIndex, pathIndex } = state;

		const result = editVerticesUseCase(
			{ originalFC, featureIndex, pathIndex, editedCoords },
			(fc) => applyFC(jobId, fc),
			(fc) => applyFC(jobId, fc),
		);

		if (result.ok) {
			deps.undoStack.push(result.data.command);
		}

		deps.drawEngine.clear();
		deps.drawEngine.setMode("static");
	}

	function deactivate() {
		state = null;
		deps.drawEngine.clear();
		deps.drawEngine.setMode("static");
		deps.toolOverlay.clearOverlay();
	}

	return {
		id: "edit-vertices",
		label: "Edit Vertices",
		cursor: "pointer",
		onActivate: activate,
		onDeactivate: deactivate,
		onMapDblClick,
		save,
	};
}
